import { useState, useMemo, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const MIN_AGE = 21;

function ageFromDob(dobStr) {
  if (!dobStr) return null;
  const dob = new Date(dobStr + "T00:00:00");
  if (Number.isNaN(dob.getTime())) return null;
  const t = new Date();
  let a = t.getFullYear() - dob.getFullYear();
  const m = t.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < dob.getDate())) a--;
  return a;
}

export default function VerifyAge() {
  const { user, loading, verifyAge, logout } = useAuth();
  const [dob, setDob] = useState("");
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const age = useMemo(() => ageFromDob(dob), [dob]);
  const dobInvalid = dob && age !== null && age < MIN_AGE;
  const maxDob = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - MIN_AGE);
    return d.toISOString().slice(0, 10);
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login", { replace: true }); return; }
    // Already verified — skip page
    if (user.date_of_birth && user.age_verified_at) {
      nav("/dashboard", { replace: true });
    }
  }, [user, loading, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (dobInvalid || !ageConfirmed) {
      toast.error(`You must be at least ${MIN_AGE} and confirm the checkbox to continue.`);
      return;
    }
    setBusy(true);
    try {
      await verifyAge(dob, ageConfirmed);
      toast.success("Thanks — your account is now active.");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    }
    setBusy(false);
  };

  const canSubmit = dob && !dobInvalid && ageConfirmed;

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="verify-age-page">
      <h1 className="font-heading text-4xl mb-2">One quick step.</h1>
      <p className="text-neutral-600 mb-8">
        Offline &amp; Available events are for adults {MIN_AGE} and older. Please confirm your date of birth to continue.
      </p>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="verify-age-dob" className="block text-sm text-neutral-600 mb-1.5">Date of birth</label>
          <Input
            id="verify-age-dob"
            type="date"
            max={maxDob}
            value={dob}
            onChange={e=>setDob(e.target.value)}
            required
            data-testid="verify-age-dob"
            className="rounded-xl"
          />
          {dobInvalid && (
            <p className="mt-1.5 text-sm text-red-600" data-testid="verify-age-dob-error">
              You must be at least {MIN_AGE} to use Offline &amp; Available.
            </p>
          )}
        </div>
        <label className="flex items-start gap-3 text-sm text-neutral-700 cursor-pointer">
          <input
            type="checkbox"
            checked={ageConfirmed}
            onChange={e=>setAgeConfirmed(e.target.checked)}
            required
            data-testid="verify-age-checkbox"
            className="mt-1 h-4 w-4 rounded border-neutral-400 accent-black"
          />
          <span>
            I confirm I am {MIN_AGE} years or older and agree to the{" "}
            <Link to="/terms" className="underline">Terms &amp; Conditions</Link>.
          </span>
        </label>
        <Button type="submit" disabled={busy || !canSubmit} className="w-full rounded-full bg-black text-white hover:bg-neutral-800 btn-pill" data-testid="verify-age-submit">
          {busy ? "Verifying…" : "Confirm and continue"}
        </Button>
      </form>
      <button
        type="button"
        onClick={async () => { await logout(); nav("/"); }}
        data-testid="verify-age-logout"
        className="mt-6 w-full text-sm text-neutral-500 underline"
      >
        Sign out instead
      </button>
    </div>
  );
}
